import React, { useEffect } from "react";
// TRANSLATION
import { useTranslation } from "react-i18next";
// ANT DESIGN COMPONENTS
import { Select, Switch, Divider, Tooltip } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
// FLATBUFFER ENUMS
import { SecurityPolicy } from "../../../../../flatbuffers/generated/opc-ua/security-policy";
import { MessageSecurityMode } from "../../../../../flatbuffers/generated/opc-ua/message-security-mode";
// COMPONENTS
import OpcUaTextField from "./OpcUaTextField";
import OpcUaFieldLabel from "./OpcUaFieldLabel";
import EnumHelpTable, { type EnumHelpRow } from "./EnumHelpTable";
// INTERFACES
import type { OpcUaCertificateConfigForm } from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Option } = Select;

/** Human-readable labels for the generated SecurityPolicy enum. */
const SECURITY_POLICY_OPTIONS: { value: SecurityPolicy; label: string }[] = [
  { value: SecurityPolicy.None_, label: "None" },
  { value: SecurityPolicy.Basic128Rsa15, label: "Basic128Rsa15" },
  { value: SecurityPolicy.Basic256, label: "Basic256" },
  { value: SecurityPolicy.Basic256Sha256, label: "Basic256Sha256" },
  { value: SecurityPolicy.Aes128Sha256RsaOaep, label: "Aes128Sha256RsaOaep" },
  { value: SecurityPolicy.Aes256Sha256RsaPss, label: "Aes256Sha256RsaPss" },
];

/** Human-readable labels for the generated MessageSecurityMode enum. */
const SECURITY_MODE_OPTIONS: { value: MessageSecurityMode; label: string }[] = [
  { value: MessageSecurityMode.None_, label: "None" },
  { value: MessageSecurityMode.Sign, label: "Sign" },
  { value: MessageSecurityMode.SignAndEncrypt, label: "SignAndEncrypt" },
];

const BLANK_CERTIFICATE: OpcUaCertificateConfigForm = {
  applicationCertPath: "",
  privateKeyPath: "",
  trustListDir: "",
  rejectListDir: "",
  trustServerCertificate: false,
  createKeypairIfMissing: true,
  verifyServerHostname: true,
};

export default function SecurityEditor({
  securityPolicy,
  securityMode,
  certificate,
  onChangeSecurityPolicy,
  onChangeSecurityMode,
  onChangeCertificate,
}: {
  securityPolicy: SecurityPolicy;
  securityMode: MessageSecurityMode;
  certificate: OpcUaCertificateConfigForm | null;
  onChangeSecurityPolicy: (value: SecurityPolicy) => void;
  onChangeSecurityMode: (value: MessageSecurityMode) => void;
  onChangeCertificate: (value: OpcUaCertificateConfigForm | null) => void;
}) {
  const [t] = useTranslation("global");

  const requiresCertificate = securityMode !== MessageSecurityMode.None_;
  // Security Mode = None means the OPC UA secure channel applies no signing
  // or encryption at all, so a Security Policy (the algorithm suite) and a
  // certificate are both meaningless — neither is ever used on the wire.
  // Disable both controls and force them back to their "off" value so a
  // stale Policy/Certificate picked before switching to None can't be saved
  // alongside it (that combination is what the OPC UA server sees as
  // invalid and silently refuses).
  const modeIsNone = securityMode === MessageSecurityMode.None_;

  useEffect(() => {
    if (!modeIsNone) return;
    if (securityPolicy !== SecurityPolicy.None_) onChangeSecurityPolicy(SecurityPolicy.None_);
    if (certificate !== null) onChangeCertificate(null);
  }, [modeIsNone, securityPolicy, certificate, onChangeSecurityPolicy, onChangeCertificate]);

  const onToggleCertificateEnabled = (enabled: boolean) => {
    onChangeCertificate(enabled ? (certificate ?? BLANK_CERTIFICATE) : null);
  };

  const onChangeCertificateField = (
    value: string | boolean,
    field: keyof OpcUaCertificateConfigForm,
  ) => {
    const base = certificate ?? BLANK_CERTIFICATE;
    onChangeCertificate({ ...base, [field]: value });
  };

  const modeHelpRows: EnumHelpRow[] = [
    { value: "None", meaning: t("general.opcUaSecurityModeNoneDesc") },
    { value: "Sign", meaning: t("general.opcUaSecurityModeSignDesc") },
    { value: "SignAndEncrypt", meaning: t("general.opcUaSecurityModeSignAndEncryptDesc") },
  ];

  const policyHelpRows: EnumHelpRow[] = [
    { value: "None", meaning: t("general.opcUaSecurityPolicyNoneMeaning") },
    {
      value: "Basic128Rsa15",
      meaning: t("general.opcUaSecurityPolicyBasic128Rsa15Meaning"),
      note: t("general.opcUaSecurityPolicyBasic128Rsa15Note"),
    },
    {
      value: "Basic256",
      meaning: t("general.opcUaSecurityPolicyBasic256Meaning"),
      note: t("general.opcUaSecurityPolicyBasic256Note"),
    },
    {
      value: "Basic256Sha256",
      meaning: t("general.opcUaSecurityPolicyBasic256Sha256Meaning"),
      note: t("general.opcUaSecurityPolicyBasic256Sha256Note"),
    },
    {
      value: "Aes128Sha256RsaOaep",
      meaning: t("general.opcUaSecurityPolicyAes128Sha256RsaOaepMeaning"),
      note: t("general.opcUaSecurityPolicyAes128Sha256RsaOaepNote"),
    },
    {
      value: "Aes256Sha256RsaPss",
      meaning: t("general.opcUaSecurityPolicyAes256Sha256RsaPssMeaning"),
      note: t("general.opcUaSecurityPolicyAes256Sha256RsaPssNote"),
    },
  ];

  return (
    <div className="container mt-3" style={{ width: "100%", maxWidth: "none" }}>
      <div className="row">
        <div className="col-12 col-md-6 mt-3">
          <span className="generalStyles__inputFlexRequired">*</span>
          <span className="generalStyles__inputLabel">
            Security Mode
            <Tooltip
              title={
                <EnumHelpTable
                  valueHeader={t("general.opcUaEnumTableValue")}
                  meaningHeader={t("general.opcUaEnumTableMeaning")}
                  rows={modeHelpRows}
                />
              }
              overlayStyle={{ maxWidth: 520 }}
            >
              <FontAwesomeIcon icon={faCircleInfo} className="generalStyles__mlFix" />
            </Tooltip>
          </span>
          <Select
            className="generalStyles__width100"
            size="large"
            value={securityMode}
            onChange={onChangeSecurityMode}
            style={{ marginTop: 8 }}
          >
            {SECURITY_MODE_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="col-12 col-md-6 mt-3">
          <span className="generalStyles__inputFlexRequired">*</span>
          <span className="generalStyles__inputLabel">
            Security Policy
            <Tooltip
              title={
                <EnumHelpTable
                  valueHeader={t("general.opcUaEnumTableValue")}
                  meaningHeader={t("general.opcUaEnumTableMeaning")}
                  noteHeader={t("general.opcUaEnumTableNote")}
                  rows={policyHelpRows}
                />
              }
              overlayStyle={{ maxWidth: 600 }}
            >
              <FontAwesomeIcon icon={faCircleInfo} className="generalStyles__mlFix" />
            </Tooltip>
          </span>
          <Select
            className="generalStyles__width100"
            size="large"
            value={securityPolicy}
            onChange={onChangeSecurityPolicy}
            disabled={modeIsNone}
            style={{ marginTop: 8 }}
          >
            {SECURITY_POLICY_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      {requiresCertificate && !certificate && (
        <span className="generalStyles__error" style={{ fontSize: "12px" }}>
          Sign / SignAndEncrypt requires a certificate configuration below.
        </span>
      )}

      <Divider orientation="left">{t("general.opcUaApplicationInstanceCertificate")}</Divider>

      <div style={{ marginBottom: 16 }}>
        <OpcUaFieldLabel label="Configure certificate" />
        <div style={{ marginTop: 8 }}>
          <Switch
            checked={certificate !== null}
            onChange={onToggleCertificateEnabled}
            disabled={modeIsNone}
          />
        </div>
      </div>
      {certificate && (
        <div className="container">
          <div className="row">
            <div className="col-12 col-md-6 mt-3">
              <OpcUaTextField
                label="Application Cert Path"
                value={certificate.applicationCertPath}
                onChange={(value) => onChangeCertificateField(value, "applicationCertPath")}
                tooltip={t("general.opcUaCertAppCertPathHelp")}
              />
            </div>
            <div className="col-12 col-md-6 mt-3">
              <OpcUaTextField
                label="Private Key Path"
                value={certificate.privateKeyPath}
                onChange={(value) => onChangeCertificateField(value, "privateKeyPath")}
                tooltip={t("general.opcUaCertPrivateKeyPathHelp")}
              />
            </div>
            <div className="col-12 col-md-6 mt-3">
              <OpcUaTextField
                label="Trust List Directory"
                value={certificate.trustListDir}
                onChange={(value) => onChangeCertificateField(value, "trustListDir")}
                tooltip={t("general.opcUaCertTrustListDirHelp")}
              />
            </div>
            <div className="col-12 col-md-6 mt-3">
              <OpcUaTextField
                label="Reject List Directory"
                value={certificate.rejectListDir}
                onChange={(value) => onChangeCertificateField(value, "rejectListDir")}
                tooltip={t("general.opcUaCertRejectListDirHelp")}
              />
            </div>
            <div className="col-12 col-md-4 mt-3">
              <OpcUaFieldLabel
                label="Trust Server Certificate"
                tooltip={t("general.opcUaCertTrustServerCertificateHelp")}
              />
              <div style={{ marginTop: 8 }}>
                <Switch
                  checked={certificate.trustServerCertificate}
                  onChange={(checked) =>
                    onChangeCertificateField(checked, "trustServerCertificate")
                  }
                />
              </div>
            </div>
            <div className="col-12 col-md-4 mt-3">
              <OpcUaFieldLabel
                label="Create Keypair If Missing"
                tooltip={t("general.opcUaCertCreateKeypairIfMissingHelp")}
              />
              <div style={{ marginTop: 8 }}>
                <Switch
                  checked={certificate.createKeypairIfMissing}
                  onChange={(checked) =>
                    onChangeCertificateField(checked, "createKeypairIfMissing")
                  }
                />
              </div>
            </div>
            <div className="col-12 col-md-4 mt-3">
              <OpcUaFieldLabel
                label="Verify Server Hostname"
                tooltip={t("general.opcUaCertVerifyServerHostnameHelp")}
              />
              <div style={{ marginTop: 8 }}>
                <Switch
                  checked={certificate.verifyServerHostname}
                  onChange={(checked) => onChangeCertificateField(checked, "verifyServerHostname")}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
